import React, { useState, useEffect } from 'react';
import { loadConnectAndInitialize } from '@stripe/connect-js';
import { ConnectComponentsProvider, ConnectAccountManagement } from '@stripe/react-connect-js';
import { createAccountSession, getConnectedAccount } from '../../api/stripeConnect';
import config from '../../config';
import { checkPaymentAPIAvailability, getPaymentPermissionErrorMessage, getPaymentPermissionSolutions, isInIframe } from '../../utils/helpers';
// import { loadStripe } from '@stripe/stripe-js';
// import { Elements, CardElement, useStripe, useElements } from '@stripe/react-stripe-js';
// import PaymentForm from './PaymentForm';

interface PaymentAccountDetailsProps {
  communityId?: string;
  userId?: string;
  onClose?: () => void;
  enableExternalAccountCollection?: boolean;
  disableStripeUserAuthentication?: boolean;
}

const PaymentAccountDetails: React.FC<PaymentAccountDetailsProps> = ({ 
  communityId, 
  userId, 
  onClose,
  enableExternalAccountCollection = true,
  disableStripeUserAuthentication = false
}) => {
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string>('');
  const [permissionsBlocked, setPermissionsBlocked] = useState(false);
  const [isEmbedded, setIsEmbedded] = useState(false);
  const [stripeConnectInstance, setStripeConnectInstance] = useState<any>(null);
  // const [accountId, setAccountId] = useState<string>('');

  useEffect(() => {
    const initializePayments = async (retryCount = 0) => {
      // Check if we're embedded in an iframe
      const embedded = isInIframe();
      setIsEmbedded(embedded);

      // Check secure context but allow localhost development
      if (typeof window !== 'undefined' && !window.isSecureContext) {
        // Only block if not localhost
        const isLocalhost = window.location.hostname === 'localhost' || 
                           window.location.hostname === '127.0.0.1' ||
                           window.location.hostname === '[::1]';
        
        if (!isLocalhost) {
          setPermissionsBlocked(true);
          setError('Payment features require a secure context (HTTPS or localhost).');
          setIsLoading(false);
          return;
        }
      }

      // Check payment permissions but don't block iframe usage
      const hasPaymentPermissions = await checkPaymentAPIAvailability();
      if (!hasPaymentPermissions) {
        // Only block if not in iframe (iframe users should still be able to try)
        if (!embedded) {
          setPermissionsBlocked(true);
          setError(getPaymentPermissionErrorMessage());
          setIsLoading(false);
          return;
        }
        // If in iframe, just warn but continue
        console.warn('Payment permissions check failed, but continuing for iframe context');
      }

      // If communityId and userId are provided, use them to get the account
      if (communityId && userId) {
        try {
          // Get the connected account for this community and user
          const accountData = await getConnectedAccount(communityId, userId);
          if (!accountData || !accountData.accountId) {
            setError('No connected account found for this community and user');
            setIsLoading(false);
            return;
          }

          // setAccountId(accountData.accountId);

          // Create AccountSession with account_management component enabled
          const sessionData = await createAccountSession({
            accountId: accountData.accountId,
            components: {
              account_management: {
                enabled: true,
                features: {
                  external_account_collection: enableExternalAccountCollection,
                  disable_stripe_user_authentication: disableStripeUserAuthentication,
                },
              },
            },
          });

          // Render only if the component is enabled in the session
          const isEnabled = sessionData?.components?.account_management?.enabled === true;
          if (!isEnabled) {
            setError('Account management is not enabled for this account.');
            setIsLoading(false);
            return;
          }

          // Determine publishable key
          const publishableKey = config.stripePublishableKey || process.env.REACT_APP_STRIPE_PUBLISHABLE_KEY || '';
          if (!publishableKey) {
            setError('Stripe publishable key is not configured. Please set REACT_APP_STRIPE_PUBLISHABLE_KEY in your environment.');
            setIsLoading(false);
            return;
          }

          // Initialize Connect.js with error handling
          try {
            const fetchClientSecret = async () => {
              const fresh = await createAccountSession({
                accountId: accountData.accountId,
                components: {
                  account_management: {
                    enabled: true,
                    features: {
                      external_account_collection: enableExternalAccountCollection,
                      disable_stripe_user_authentication: disableStripeUserAuthentication,
                    },
                  },
                },
              });
              return fresh.client_secret;
            };

            const connectInstance = await loadConnectAndInitialize({
              publishableKey,
              fetchClientSecret,
              appearance: {
                variables: {
                  colorPrimary: '#3b82f6',
                  colorBackground: '#ffffff',
                  colorText: '#1f2937',
                  colorDanger: '#ef4444',
                  fontFamily: 'system-ui, sans-serif',
                  spacingUnit: '8px',
                  borderRadius: '8px',
                },
              },
            });

            setStripeConnectInstance(connectInstance);
            setIsLoading(false);
          } catch (connectError) {
            console.error('Connect.js initialization error:', connectError);
            
            // Check if it's a permissions policy violation
            if (connectError instanceof Error && 
                (connectError.message.includes('permissions') || 
                 connectError.message.includes('payment') ||
                 connectError.message.includes('policy'))) {
              setPermissionsBlocked(true);
              setError(getPaymentPermissionErrorMessage('Please try opening this page in a new tab.'));
            } else {
              throw connectError; // Re-throw other errors to be handled by the general error handler
            }
            setIsLoading(false);
          }
        } catch (err) {
          console.error('Error initializing payments:', err);
          
          // Handle different types of errors
          if (err instanceof Error) {
            if (err.message.includes('Account ID')) {
              setError('Invalid account configuration. Please contact support.');
            } else if (err.message.includes('Failed to create')) {
              setError('Unable to create payment session. Please try again.');
            } else if (err.message.includes('network')) {
              setError('Network error. Please check your connection and try again.');
            } else if (err.message.includes('permissions') || err.message.includes('policy')) {
              setPermissionsBlocked(true);
              setError(getPaymentPermissionErrorMessage());
            } else {
              setError(err.message);
            }
          } else {
            setError('An unexpected error occurred. Please try again.');
          }
          setIsLoading(false);
        }
      } else {
        // If no communityId/userId provided, show error
        setError('Community ID and User ID are required to access payment dashboard');
        setIsLoading(false);
      }
    };

    initializePayments();

    // Cleanup function
    return () => {
      setStripeConnectInstance(null);
    };
  }, [communityId, userId, enableExternalAccountCollection, disableStripeUserAuthentication]);

  if (isLoading) {
    return (
      <div className="flex items-center justify-center min-h-[400px]">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary-400 mx-auto mb-4"></div>
          <p className="text-gray-600">Loading account management dashboard...</p>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="bg-red-50 border border-red-200 rounded-lg p-6">
        <div className="flex items-center">
          <div className="flex-shrink-0">
            <svg className="h-5 w-5 text-red-400" viewBox="0 0 20 20" fill="currentColor">
              <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zM8.707 7.293a1 1 0 00-1.414 1.414L8.586 10l-1.293 1.293a1 1 0 101.414 1.414L10 11.414l1.293 1.293a1 1 0 001.414-1.414L11.414 10l1.293-1.293a1 1 0 00-1.414-1.414L10 8.586 8.707 7.293z" clipRule="evenodd" />
            </svg>
          </div>
          <div className="ml-3">
            <h3 className="text-sm font-medium text-red-800">
              {permissionsBlocked ? 'Payment Features Blocked' : 'Error Loading Account Management'}
            </h3>
            {/* <p className="text-sm text-red-700 mt-1">{error}</p> */}
            {permissionsBlocked && (
              <div className="mt-3 text-sm text-red-600">
                <p className="font-medium">Possible solutions:</p>
                <ul className="list-disc list-inside mt-1 space-y-1">
                  {getPaymentPermissionSolutions().map((solution, index) => (
                    <li key={index}>{solution}</li>
                  ))}
                </ul>
                
                {isEmbedded && (
                  <div className="mt-3 p-3 bg-yellow-50 border border-yellow-200 rounded">
                    <p className="font-medium text-yellow-800">🚨 Iframe Detected</p>
                    <p className="text-yellow-700 text-xs mt-1">
                      This page is embedded in an iframe. To enable payment features, the parent iframe must include:
                    </p>
                    <code className="block mt-2 p-2 bg-yellow-100 text-yellow-800 text-xs rounded font-mono">
                      allow="payment *" allowpaymentrequest
                    </code>
                    <p className="text-yellow-700 text-xs mt-2">
                      Ask the developer of the parent page to add these attributes to the iframe element.
                    </p>
                  </div>
                )}
              </div>
            )}
          </div>
        </div>
        {onClose && (
          <button
            onClick={onClose}
            className="mt-4 bg-red-100 text-red-800 px-4 py-2 rounded hover:bg-red-200"
          >
            Close
          </button>
        )}
      </div>
    );
  }

  return (
    <div className="">
      {/* Iframe Warning Banner */}
      {isEmbedded && (
        <div className="mb-4 p-3 bg-amber-50 border border-amber-200 rounded-lg">
          <div className="flex items-start">
            <div className="flex-shrink-0">
              <svg className="h-5 w-5 text-amber-400" viewBox="0 0 20 20" fill="currentColor">
                <path fillRule="evenodd" d="M8.257 3.099c.765-1.36 2.722-1.36 3.486 0l5.58 9.92c.75 1.334-.213 2.98-1.742 2.98H4.42c-1.53 0-2.493-1.646-1.743-2.98l5.58-9.92zM11 13a1 1 0 11-2 0 1 1 0 012 0zm-1-8a1 1 0 00-1 1v3a1 1 0 002 0V6a1 1 0 00-1-1z" clipRule="evenodd" />
              </svg>
            </div>
            <div className="ml-3">
              <h3 className="text-sm font-medium text-amber-800">Iframe Detected</h3>
              <p className="text-sm text-amber-700 mt-1">
                This page is embedded in an iframe. Payment features may be blocked unless the parent iframe includes proper permissions.
              </p>
              <div className="mt-2">
                <p className="text-xs text-amber-600 font-medium">Required iframe attributes:</p>
                <code className="block mt-1 p-2 bg-amber-100 text-amber-800 text-xs rounded font-mono">
                  allow="payment *" allowpaymentrequest
                </code>
              </div>
            </div>
          </div>
        </div>
      )}

      <div className="flex items-center justify-between">
        {/* <h2 className="text-xl font-semibold text-gray-900">Account Management Dashboard</h2> */}
        {onClose && (
          <button
            onClick={onClose}
            className="text-gray-400 hover:text-gray-600"
          >
            <svg className="h-6 w-6" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
            </svg>
          </button>
        )}
      </div>
      
      <div className="bg-white border rounded-lg overflow-hidden ">
        <div className="p-4 border-b bg-gray-50">
          <p className="text-sm text-gray-600">
            View and edit your account details, business information, and bank accounts for payouts.
          </p>
        </div>
        
        {/* Stripe Connect Account Management via React components */}
        <div className="min-h-[600px] px-4 py-2">
          {stripeConnectInstance && (
            <ConnectComponentsProvider connectInstance={stripeConnectInstance}>
              <ConnectAccountManagement 
                collectionOptions={{
                  fields: 'eventually_due',
                  futureRequirements: 'include',
                }}
              />
            </ConnectComponentsProvider>
          )}
        </div>
      </div>

      <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
        <h3 className="text-sm font-medium text-blue-800 mb-2">Available Features</h3>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-2 text-sm text-blue-700">
          <div className="flex items-center">
            <span className="w-2 h-2 bg-blue-500 rounded-full mr-2"></span>
            <span>View and edit personal information</span>
          </div>
          <div className="flex items-center">
            <span className="w-2 h-2 bg-blue-500 rounded-full mr-2"></span>
            <span>Update business details</span>
          </div>
          <div className="flex items-center">
            <span className="w-2 h-2 bg-blue-500 rounded-full mr-2"></span>
            <span>Manage bank accounts for payouts</span>
          </div>
          <div className="flex items-center">
            <span className="w-2 h-2 bg-blue-500 rounded-full mr-2"></span>
            <span>Update public information</span>
          </div>
          {enableExternalAccountCollection && (
            <div className="flex items-center">
              <span className="w-2 h-2 bg-blue-500 rounded-full mr-2"></span>
              <span>Add external bank accounts</span>
            </div>
          )}
          {!disableStripeUserAuthentication && (
            <div className="flex items-center">
              <span className="w-2 h-2 bg-blue-500 rounded-full mr-2"></span>
              <span>Secure Stripe authentication</span>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default PaymentAccountDetails; 